import type { ReactNode, Ref } from 'react'
import { forwardRef } from 'react'
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'

import loiInsightEditBoxIcon from '@/assets/loi-dashboard/loi-insight-edit-box-icon.svg'
import loiInsightRotateRightIcon from '@/assets/loi-dashboard/loi-insight-rotate-right-icon.svg'
import reEvaluateIcon from '@/assets/loi-dashboard/re-evaluate-icon.svg'

export type InsightActionButtonsSharedProps = {
  canWrite: boolean
  reEvaluatePending?: boolean
  onReEvaluate: () => void
  onAddNote: () => void
  reEvaluateTestId: string
  addNoteTestId: string
  showReEvaluate?: boolean
  showAddNote?: boolean
  reEvaluateTooltip?: string
  addNoteTooltip?: string
  viewerReEvaluateTooltip?: string
  viewerAddNoteTooltip?: string
}

const buttonBaseSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  height: 25,
  minHeight: 25,
  maxHeight: 25,
  m: 0,
  borderRadius: '6px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: '10px',
  lineHeight: '15px',
  letterSpacing: 0,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  WebkitAppearance: 'none',
  '&:disabled': {
    opacity: 0.38,
    cursor: 'not-allowed',
  },
} as const

const headerIconButtonSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  width: 32,
  height: 32,
  minWidth: 32,
  m: 0,
  p: 0,
  borderRadius: '8px',
  border: '1px solid #C4B5FD',
  bgcolor: '#FFFFFF',
  color: '#7C3AED',
  cursor: 'pointer',
  WebkitAppearance: 'none',
  flexShrink: 0,
  '&:hover:not(:disabled)': {
    bgcolor: 'rgba(124, 58, 237, 0.06)',
  },
  '&:disabled': {
    opacity: 0.38,
    cursor: 'not-allowed',
  },
} as const

const ActionButton = forwardRef(function ActionButton(
  {
    children,
    disabled,
    testId,
    onClick,
    sx,
    ariaLabel,
    ...rest
  }: Readonly<{
    children: ReactNode
    disabled?: boolean
    testId: string
    onClick: () => void
    sx: object
    ariaLabel: string
  }>,
  ref: Ref<HTMLButtonElement>,
) {
  return (
    <Box
      ref={ref}
      component="button"
      type="button"
      disabled={disabled}
      data-testid={testId}
      onClick={onClick}
      aria-label={ariaLabel}
      sx={{ ...buttonBaseSx, ...sx }}
      {...rest}
    >
      {children}
    </Box>
  )
})

function InsightActionTooltip({
  title,
  disabled,
  children,
}: Readonly<{
  title: string
  disabled?: boolean
  children: React.ReactElement
}>) {
  if (disabled) {
    return (
      <Tooltip title={title}>
        <Box component="span" sx={{ display: 'inline-flex' }}>
          {children}
        </Box>
      </Tooltip>
    )
  }
  return <Tooltip title={title}>{children}</Tooltip>
}

function tooltipTitle(canWrite: boolean, viewer: string, enabled: string) {
  return canWrite ? enabled : viewer
}

export function LoiHeaderInsightActionButtons({
  canWrite,
  reEvaluatePending = false,
  onReEvaluate,
  onAddNote,
  reEvaluateTestId,
  addNoteTestId,
  showReEvaluate = true,
  showAddNote = true,
  reEvaluateTooltip = 'Re-evaluate using all current responses',
  addNoteTooltip = 'Add Note',
  viewerReEvaluateTooltip = 'Available to Admin and Editor only',
  viewerAddNoteTooltip = 'Add Note is available to Admin and Editor only',
}: Readonly<InsightActionButtonsSharedProps>) {
  const loiIconButtonSx = {
    width: 16,
    height: 16,
    minWidth: 16,
    minHeight: 16,
    p: 0,
    border: 'none',
    bgcolor: 'transparent',
    borderRadius: 0,
    flexShrink: 0,
    '&:hover:not(:disabled)': {
      bgcolor: 'transparent',
      opacity: 0.75,
    },
  } as const

  return (
    <Stack direction="row" alignItems="center" spacing="8px" sx={{ flexShrink: 0 }}>
      {showAddNote ? (
        <InsightActionTooltip
          title={tooltipTitle(canWrite, viewerAddNoteTooltip, addNoteTooltip)}
          disabled={!canWrite}
        >
          <ActionButton
            testId={addNoteTestId}
            disabled={!canWrite}
            onClick={onAddNote}
            ariaLabel={addNoteTooltip}
            sx={{ ...loiIconButtonSx, color: '#008CD0' }}
          >
            <Box
              component="img"
              src={loiInsightEditBoxIcon}
              alt=""
              aria-hidden
              sx={{ width: 16, height: 16, display: 'block' }}
            />
          </ActionButton>
        </InsightActionTooltip>
      ) : null}
      {showReEvaluate ? (
        <InsightActionTooltip
          title={tooltipTitle(canWrite, viewerReEvaluateTooltip, reEvaluateTooltip)}
          disabled={!canWrite || reEvaluatePending}
        >
          <ActionButton
            testId={reEvaluateTestId}
            disabled={!canWrite || reEvaluatePending}
            onClick={onReEvaluate}
            ariaLabel={reEvaluateTooltip}
            sx={{ ...loiIconButtonSx, color: '#7E22CE' }}
          >
            {reEvaluatePending ? (
              <CircularProgress size={14} sx={{ color: '#7E22CE' }} aria-hidden />
            ) : (
              <Box
                component="img"
                src={loiInsightRotateRightIcon}
                alt=""
                aria-hidden
                sx={{ width: 16, height: 16, display: 'block' }}
              />
            )}
          </ActionButton>
        </InsightActionTooltip>
      ) : null}
    </Stack>
  )
}

export function HeaderIconInsightActionButtons({
  canWrite,
  reEvaluatePending = false,
  onReEvaluate,
  onAddNote,
  reEvaluateTestId,
  addNoteTestId,
  showReEvaluate = true,
  showAddNote = true,
  reEvaluateTooltip = 'Re-evaluate using all current responses',
  addNoteTooltip = 'Add Note',
  viewerReEvaluateTooltip = 'Available to Admin and Editor only',
  viewerAddNoteTooltip = 'Add Note is available to Admin and Editor only',
}: Readonly<InsightActionButtonsSharedProps>) {
  return (
    <Stack direction="row" alignItems="center" spacing="8px" sx={{ flexShrink: 0 }}>
      {showAddNote ? (
        <InsightActionTooltip
          title={tooltipTitle(canWrite, viewerAddNoteTooltip, addNoteTooltip)}
          disabled={!canWrite}
        >
          <ActionButton
            testId={addNoteTestId}
            disabled={!canWrite}
            onClick={onAddNote}
            ariaLabel={addNoteTooltip}
            sx={headerIconButtonSx}
          >
            <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} aria-hidden />
          </ActionButton>
        </InsightActionTooltip>
      ) : null}
      {showReEvaluate ? (
        <InsightActionTooltip
          title={tooltipTitle(canWrite, viewerReEvaluateTooltip, reEvaluateTooltip)}
          disabled={!canWrite || reEvaluatePending}
        >
          <ActionButton
            testId={reEvaluateTestId}
            disabled={!canWrite || reEvaluatePending}
            onClick={onReEvaluate}
            ariaLabel={reEvaluateTooltip}
            sx={headerIconButtonSx}
          >
            {reEvaluatePending ? (
              <CircularProgress size={14} sx={{ color: '#7C3AED' }} aria-hidden />
            ) : (
              <AutorenewOutlinedIcon sx={{ fontSize: 16 }} aria-hidden />
            )}
          </ActionButton>
        </InsightActionTooltip>
      ) : null}
    </Stack>
  )
}

export function PillInsightActionButtons({
  canWrite,
  reEvaluatePending = false,
  onReEvaluate,
  onAddNote,
  reEvaluateTestId,
  addNoteTestId,
  showReEvaluate = true,
  showAddNote = true,
  reEvaluateTooltip = 'Re-evaluate using all current responses',
  addNoteTooltip = 'Add Note',
  viewerReEvaluateTooltip = 'Available to Admin and Editor only',
  viewerAddNoteTooltip = 'Add Note is available to Admin and Editor only',
}: Readonly<InsightActionButtonsSharedProps>) {
  return (
    <Stack direction="row" alignItems="center" sx={{ pt: '4.7px' }}>
      {showReEvaluate ? (
        <InsightActionTooltip
          title={tooltipTitle(canWrite, viewerReEvaluateTooltip, reEvaluateTooltip)}
          disabled={!canWrite || reEvaluatePending}
        >
          <ActionButton
            testId={reEvaluateTestId}
            disabled={!canWrite || reEvaluatePending}
            onClick={onReEvaluate}
            ariaLabel={reEvaluateTooltip}
            sx={{
              gap: '4px',
              pl: '8px',
              pr: '11px',
              bgcolor: '#FFF7ED',
              border: '1px solid #FED7AA',
              color: '#C2410C',
            }}
          >
            {reEvaluatePending ? (
              <CircularProgress size={10} sx={{ color: '#C2410C', flexShrink: 0 }} aria-hidden />
            ) : (
              <Box
                component="img"
                src={reEvaluateIcon}
                alt=""
                aria-hidden
                sx={{ width: 10, height: 10, display: 'block', flexShrink: 0 }}
              />
            )}
            Re-Evaluate
          </ActionButton>
        </InsightActionTooltip>
      ) : null}
      {showAddNote ? (
        <InsightActionTooltip
          title={tooltipTitle(canWrite, viewerAddNoteTooltip, addNoteTooltip)}
          disabled={!canWrite}
        >
          <ActionButton
            testId={addNoteTestId}
            disabled={!canWrite}
            onClick={onAddNote}
            ariaLabel={addNoteTooltip}
            sx={{
              ml: showReEvaluate ? '8px' : 0,
              gap: '3px',
              pl: '6px',
              pr: '11px',
              bgcolor: '#E3EFFB',
              border: '1px solid #1976D2',
              color: '#1976D2',
            }}
          >
            <Box
              component="span"
              aria-hidden
              sx={{
                fontSize: '10px',
                lineHeight: '15px',
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              📝
            </Box>
            Add Note
          </ActionButton>
        </InsightActionTooltip>
      ) : null}
    </Stack>
  )
}
